import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightning } from "@phosphor-icons/react";
import { useGamification } from "../hooks/useGamification";
import { CONFETTI_COLORS } from "../utils/gamificationHelpers";

function ConfettiParticle({ index }: { index: number }) {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = Math.random() * 100;
  const delay = Math.random() * 0.5;
  const duration = 2.5 + Math.random() * 1.5;
  const size = 8 + Math.random() * 8;
  const drift = (Math.random() - 0.5) * 200;

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: `${left}%`,
        top: -20,
        width: size,
        height: size * (0.4 + Math.random() * 0.6),
        backgroundColor: color,
        borderRadius: Math.random() > 0.5 ? "50%" : "2px",
      }}
      initial={{ y: -20, x: 0, rotate: 0, opacity: 1 }}
      animate={{
        y: [0, window.innerHeight + 50],
        x: [0, drift],
        rotate: [0, 360 * (Math.random() > 0.5 ? 1 : -1)],
        opacity: [1, 1, 0],
      }}
      transition={{ duration, delay, ease: "easeIn" }}
    />
  );
}

export default function LevelUpOverlay() {
  const { stats, isSuccess } = useGamification();
  const prevLevel = useRef<number | null>(null);
  const [show, setShow] = useState(false);
  const [newLevel, setNewLevel] = useState(0);

  useEffect(() => {
    if (!isSuccess) return;

    // İlk yüklemede sadece kaydet, gösterme
    if (prevLevel.current === null) {
      prevLevel.current = stats.level;
      return;
    }

    // Gerçek level artışı
    if (stats.level > prevLevel.current) {
      setNewLevel(stats.level);
      setShow(true);
    }
    prevLevel.current = stats.level;
  }, [stats.level, isSuccess]);

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => setShow(false), 4500);
    return () => clearTimeout(timer);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[110] flex items-center justify-center cursor-pointer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setShow(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Confetti */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {Array.from({ length: 60 }).map((_, i) => (
              <ConfettiParticle key={i} index={i} />
            ))}
          </div>

          <motion.div
            className="relative z-10 text-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 14, delay: 0.1 }}
          >
            {/* Badge */}
            <motion.div
              className="w-24 h-24 rounded-full bg-myPurple/20 border-2 border-myPurple/40 flex items-center justify-center mx-auto mb-4"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.3 }}
            >
              <Lightning size={48} weight="fill" className="text-myPurple" />
            </motion.div>

            <motion.p
              className="text-myPurple text-lg font-bold uppercase tracking-wider mb-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Seviye Atladın!
            </motion.p>

            <motion.p
              className="text-5xl font-black text-white mb-2"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.7 }}
            >
              Seviye {newLevel}
            </motion.p>

            <motion.p
              className="text-white/50 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              Harika gidiyorsun, devam et!
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
